import {
  CHAR_WIDTH_RATIO,
  FONT_SIZE_DIVISOR,
  LINE_SPACING,
  MAX_FONT_SIZE,
  MIN_CELL_HEIGHT,
  MIN_CELL_WIDTH,
  MIN_FONT_SIZE,
  TEXT_PADDING,
} from './tree-map-content.constants';

/**
 * Helper function to wrap text in SVG by breaking it into lines that fit within a given width
 *
 * @param {string} text - The text to wrap
 * @param {number} maxWidth - Maximum width available for the text
 * @param {number} fontSize - Font size to calculate character width
 * @returns {string[]} Array of text lines that fit within the maxWidth
 */
const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = '';

  // Approximate character width based on fontSize
  const charWidth = fontSize * CHAR_WIDTH_RATIO;

  // Prevent division by zero - if charWidth is 0 or negative, return text as single line
  if (charWidth <= 0 || maxWidth <= 0) {
    return text ? [text] : [];
  }

  const maxCharsPerLine = Math.floor(maxWidth / charWidth);

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;

    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Word is too long, break it
        lines.push(word.substring(0, maxCharsPerLine));
        currentLine = word.substring(maxCharsPerLine);
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

/**
 * Calculate dynamic font size based on container width
 *
 * @param {number} width - Width of the container
 * @returns {number} Calculated font size between MIN_FONT_SIZE and MAX_FONT_SIZE pixels
 */
const calculateFontSize = (width: number): number => {
  return Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, width / FONT_SIZE_DIVISOR));
};

/**
 * Calculate the maximum number of lines that can fit in a given height
 *
 * @param {number} height - Available height for text
 * @param {number} fontSize - Font size
 * @param {number} padding - Padding to account for (defaults to TEXT_PADDING)
 * @returns {number} Maximum number of lines that fit
 */
const calculateMaxLines = (height: number, fontSize: number, padding = TEXT_PADDING): number => {
  const lineHeight = fontSize + LINE_SPACING;

  // Prevent division by zero and negative results
  if (lineHeight <= 0 || height <= padding) {
    return 0;
  }

  return Math.floor((height - padding) / lineHeight);
};

/**
 * Calculate the starting Y position for vertically centered text
 *
 * @param {number} y - Y position of the container
 * @param {number} height - Height of the container
 * @param {number} lineCount - Number of text lines
 * @param {number} fontSize - Font size
 * @returns {number} Y position for the first line of text
 */
const calculateStartY = (y: number, height: number, lineCount: number, fontSize: number): number => {
  const totalTextHeight = lineCount * (fontSize + LINE_SPACING);
  return y + height / 2 - totalTextHeight / 2 + fontSize / 2;
};

/**
 * Check if text should be rendered based on container dimensions
 *
 * @param {number} width - Container width
 * @param {number} height - Container height
 * @param {number} minWidth - Minimum width required (defaults to MIN_CELL_WIDTH)
 * @param {number} minHeight - Minimum height required (defaults to MIN_CELL_HEIGHT)
 * @returns {boolean} Whether text should be rendered
 */
const shouldRenderText = (
  width: number,
  height: number,
  minWidth = MIN_CELL_WIDTH,
  minHeight = MIN_CELL_HEIGHT,
): boolean => {
  return width > minWidth && height > minHeight;
};

export { calculateFontSize, calculateMaxLines, calculateStartY, shouldRenderText, wrapText };
